import { useStageUsage } from '../hooks/useStageUsage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { archivPfadUrl, type BuehneAnsicht } from './GuideHof';
import { antwortWort } from './BuehnenKettenZeile';
import { teileNachEigentum } from './guide-partner-eigentum';

const API = '/api';
const SEITEN_GROESSE = 24;
const ARCHIV_PFAD = /(?:^|\s)(buehnen-bilder\/[a-zA-Z0-9_-][a-zA-Z0-9_.-]*\.png)(?=\s|\()/;

type AufnahmeArt = 'sichtpruefung' | 'abschluss';

export type HistorienAufnahme = {
  id: string;
  buehne: BuehneAnsicht;
  art: AufnahmeArt;
  am: string;
  pfad: string | null;
  fehlt: string | null;
  fassung: string | null;
};

type BuehnenRueckblick = { buehne: BuehneAnsicht; aufnahmen: HistorienAufnahme[] };

function datum(iso: string): string {
  const wert = new Date(iso);
  return Number.isNaN(wert.getTime()) ? 'Datum unbekannt' : wert.toLocaleString('de-AT');
}

function bildAusVerlauf(grund: string): string | null {
  return grund.match(ARCHIV_PFAD)?.[1] ?? null;
}

function aufnahmenFuer(buehne: BuehneAnsicht): HistorienAufnahme[] {
  const aufnahmen = new Map<string, HistorienAufnahme>();
  for (const eintrag of buehne.verlauf ?? []) {
    if (eintrag.was !== 'angesehen') continue;
    const pfad = bildAusVerlauf(eintrag.grund ?? '');
    const schluessel = pfad ?? `ohne-archiv:${eintrag.am}:${eintrag.von}`;
    aufnahmen.set(schluessel, {
      id: `${buehne.id}:sicht:${schluessel}`,
      buehne, art: 'sichtpruefung', am: eintrag.am, pfad,
      fehlt: pfad ? null : 'Diese ältere Sichtprüfung verweist nicht auf eine Aufnahme im gemeinsamen Bühnenarchiv.',
      fassung: null,
    });
  }

  if (buehne.gesehen) {
    const pfad = archivPfadUrl(buehne.gesehen.bild) ? buehne.gesehen.bild : null;
    const schluessel = pfad ?? `ohne-archiv:${buehne.gesehen.am}:${buehne.gesehen.von}`;
    const vorhanden = aufnahmen.get(schluessel);
    aufnahmen.set(schluessel, {
      id: `${buehne.id}:sicht:${schluessel}`,
      buehne, art: 'sichtpruefung', am: vorhanden?.am ?? buehne.gesehen.am, pfad,
      fehlt: pfad ? null : 'Die letzte Sichtprüfung nennt keine Aufnahme im gemeinsamen Bühnenarchiv.',
      fassung: buehne.gesehen.fassung ?? null,
    });
  }

  if (buehne.abschluss) {
    const pfad = buehne.bild && 'pfad' in buehne.bild && archivPfadUrl(buehne.bild.pfad) ? buehne.bild.pfad : null;
    const fehlt = buehne.bild && 'fehlt' in buehne.bild
      ? buehne.bild.fehlt
      : pfad ? null : 'Beim Abschluss wurde kein Bühnenbild gespeichert.';
    const am = buehne.bild?.am ?? buehne.abschluss.am;
    aufnahmen.set(`abschluss:${am}`, {
      id: `${buehne.id}:abschluss:${am}`,
      buehne, art: 'abschluss', am, pfad, fehlt, fassung: null,
    });
  }

  return [...aufnahmen.values()].sort((a, b) => b.am.localeCompare(a.am));
}

function rueckblickeAus(buehnen: BuehneAnsicht[]): BuehnenRueckblick[] {
  return buehnen.flatMap(buehne => {
    const aufnahmen = aufnahmenFuer(buehne);
    return aufnahmen.length ? [{ buehne, aufnahmen }] : [];
  }).sort((a, b) => b.aufnahmen[0].am.localeCompare(a.aufnahmen[0].am));
}

/*
 * STRAENGE STATT LOSER FOTOGALERIE (Rafael, 03.10.2026, Wahl a: „der Guide zeigt die Kette").
 * Ein Strang ist alles, was ueber `vorgaenger`/`nachfolger`/`weitere_nachfolger` zusammenhaengt,
 * in der Reihenfolge der Anlage. Jede Antwort springt zu der Buehne, die sie umgesetzt hat —
 * das ist die Nachfolgerin, denn seit dem 03.10. geht es nach einer Antwort immer auf einer
 * neuen Buehne weiter. Nur wo der Schalter `kette_an` an ist; sonst bleibt die Galerie wie bisher.
 */
export function strangVon(alle: Map<string, BuehneAnsicht>, id: string): BuehneAnsicht[] {
  const gesehen = new Set<string>();
  const offen = [id];
  while (offen.length) {
    const x = offen.pop()!;
    if (gesehen.has(x)) continue;
    const b = alle.get(x);
    if (!b) continue;
    gesehen.add(x);
    for (const n of [...(b.vorgaenger ?? []), b.nachfolger, ...(b.weitere_nachfolger ?? [])]) if (n && !gesehen.has(n)) offen.push(n);
  }
  return [...gesehen].map(x => alle.get(x)!).sort((a, b) => (a.angelegt_am ?? '').localeCompare(b.angelegt_am ?? '') || a.id.localeCompare(b.id));
}

/** Die Buehne, die eine Antwort auf `b` umgesetzt hat: ihre Nachfolgerin, sofern bekannt. */
export function umgesetztIn(alle: Map<string, BuehneAnsicht>, b: BuehneAnsicht): BuehneAnsicht | null {
  return b.nachfolger ? alle.get(b.nachfolger) ?? null : null;
}

/** Galerie-Reihenfolge nach Straengen: ein Strang bleibt beisammen (aelteste zuerst), Straenge nach juengster Aufnahme. */
export function nachStraengen(rueckblicke: BuehnenRueckblick[], alle: Map<string, BuehneAnsicht>): BuehnenRueckblick[] {
  const strangSchluessel = new Map<string, string>();
  for (const r of rueckblicke) {
    if (strangSchluessel.has(r.buehne.id)) continue;
    const strang = strangVon(alle, r.buehne.id);
    for (const m of strang) strangSchluessel.set(m.id, strang[0]?.id ?? r.buehne.id);
  }
  const juengste = new Map<string, string>();
  for (const r of rueckblicke) {
    const k = strangSchluessel.get(r.buehne.id) ?? r.buehne.id;
    if ((juengste.get(k) ?? '') < r.aufnahmen[0].am) juengste.set(k, r.aufnahmen[0].am);
  }
  const ordnung = new Map([...alle.values()].map(b => [b.id, b.angelegt_am ?? '']));
  return [...rueckblicke].sort((a, b) => {
    const ka = strangSchluessel.get(a.buehne.id) ?? a.buehne.id, kb = strangSchluessel.get(b.buehne.id) ?? b.buehne.id;
    if (ka !== kb) return (juengste.get(kb) ?? '').localeCompare(juengste.get(ka) ?? '') || ka.localeCompare(kb);
    return (ordnung.get(a.buehne.id) ?? '').localeCompare(ordnung.get(b.buehne.id) ?? '');
  });
}

function aufnahmeName(aufnahme: HistorienAufnahme): string {
  if (aufnahme.art === 'abschluss') return 'Abschlussstand';
  if (aufnahme.fassung) return `Geprüfte Fassung ${aufnahme.fassung.slice(0, 8)}`;
  return 'Gespeicherte Sichtprüfung';
}

function bildFehler(aufnahme: HistorienAufnahme): string {
  if (aufnahme.fehlt) return `Kein Bühnenbild: ${aufnahme.fehlt}`;
  return 'Das gespeicherte Bühnenbild ist nicht abrufbar.';
}

/** Liest nur vorhandene Sichtbelege, ältere „angesehen“-Fassungen und Abschlüsse. */
export function GuideHistorie({ startBuehneId, onSchliessen, istBetreiber = true }: {
  startBuehneId?: string | null;
  /** Rafael (Betreiber) sieht nur seine Buehnen — Partner-Buehnen zaehlen hier nicht
   *  (Rafael, 05.10.2026). Ein Partner bekommt vom Server ohnehin nur seine. */
  istBetreiber?: boolean;
  onSchliessen: () => void;
}) {
  const [rueckblicke, setRueckblicke] = useState<BuehnenRueckblick[] | null>(null);
  const [alle, setAlle] = useState<Map<string, BuehneAnsicht>>(() => new Map());
  const [ladeVersuch, setLadeVersuch] = useState(0);
  const [auswahl, setAuswahl] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bildStatus, setBildStatus] = useState<string | null>(null);
  const [bildBereit, setBildBereit] = useState(false);
  const [bildGross, setBildGross] = useState(false);
  const [defekteBilder, setDefekteBilder] = useState<Set<string>>(() => new Set());
  const [sichtbar, setSichtbar] = useState(SEITEN_GROESSE);

  useEffect(() => {
    const controller = new AbortController();
    setFehler(null);
    /*
     * `schlank=1` STATT DES NIE GELESENEN `ansicht=liste` (Rafael, 21.09.2026).
     *
     * Hier stand `?ansicht=liste&alle=1`. `ansicht` wertet die Route nicht aus — die
     * Historie bat also um eine Listenansicht, die es nicht gab, und bekam den vollen
     * Bestand. Gemessen: 15-18 Sekunden, in denen der ganze Server steht, weil Node
     * einen Thread hat. Nicht nur fuer den, der klickt: fuer jede laufende Sitzung.
     *
     * `schlank=1` kappt den Verlauf auf Raumwechsel, Anlage und SICHTBELEGE — die
     * letzten sind seit heute mit drin, genau dafuer (guide-buehnen-routen.ts,
     * `schlankeAnsicht`). Vom Archiv sind das statt 14,9 MB Verlauf noch die 2 %, die
     * `aufnahmenFuer` unten tatsaechlich liest.
     */
    fetch(`${API}/guide/buehnen?schlank=1&alle=1`, {
      signal: AbortSignal.any([controller.signal, AbortSignal.timeout(60000)]),
    }).then(async response => {
      const inhalt = await response.text();
      if (!response.headers.get('content-type')?.includes('application/json')) {
        throw new Error(`Der Bühnenbestand antwortet nicht mit Daten (HTTP ${response.status}).`);
      }
      let daten: { error?: string; buehnen?: unknown };
      try { daten = JSON.parse(inhalt); }
      catch { throw new Error(`Der Bühnenbestand enthält ungültige Daten (HTTP ${response.status}).`); }
      if (!response.ok) throw new Error(daten.error || `HTTP ${response.status}`);
      if (!Array.isArray(daten?.buehnen)) throw new Error('Antwort ohne Feld „buehnen“.');
      const liste = teileNachEigentum(daten.buehnen as BuehneAnsicht[], istBetreiber).eigene;
      const karte = new Map(liste.map(b => [b.id, b]));
      const roh = rueckblickeAus(liste);
      return { karte, rueckblick: liste.some(b => b.kette_an) ? nachStraengen(roh, karte) : roh };
    }).then(({ karte, rueckblick }) => {
      setAlle(karte);
      setRueckblicke(rueckblick);
      setAuswahl(aktuell => {
        if (aktuell && rueckblick.some(eintrag => eintrag.aufnahmen.some(aufnahme => aufnahme.id === aktuell))) return aktuell;
        const start = startBuehneId ? rueckblick.find(eintrag => eintrag.buehne.id === startBuehneId) : null;
        return (start ?? rueckblick[0])?.aufnahmen[0]?.id ?? null;
      });
    }).catch(error => {
      if (error?.name !== 'AbortError') setFehler(error?.name === 'TimeoutError'
        ? 'Die Bühnenhistorie braucht zu lange zum Laden.'
        : error instanceof Error ? error.message : String(error));
    });
    return () => controller.abort();
  }, [ladeVersuch, startBuehneId, istBetreiber]);

  const alleAufnahmen = useMemo(() => (rueckblicke ?? []).flatMap(rueckblick => rueckblick.aufnahmen)
    .sort((a, b) => b.am.localeCompare(a.am)), [rueckblicke]);
  const gewaehlt = useMemo(() => alleAufnahmen.find(aufnahme => aufnahme.id === auswahl) ?? null, [alleAufnahmen, auswahl]);
  useStageUsage(gewaehlt?.buehne.id, gewaehlt?.buehne.haus);
  const gewaehltRueckblick = useMemo(() => rueckblicke?.find(eintrag => eintrag.buehne.id === gewaehlt?.buehne.id) ?? null, [rueckblicke, gewaehlt]);
  const index = gewaehlt ? alleAufnahmen.findIndex(aufnahme => aufnahme.id === gewaehlt.id) : -1;
  const fassungsIndex = gewaehlt && gewaehltRueckblick
    ? gewaehltRueckblick.aufnahmen.findIndex(aufnahme => aufnahme.id === gewaehlt.id) : -1;
  const buehnenIndex = gewaehltRueckblick && rueckblicke
    ? rueckblicke.findIndex(eintrag => eintrag.buehne.id === gewaehltRueckblick.buehne.id) : -1;

  useEffect(() => {
    if (buehnenIndex >= sichtbar) setSichtbar(Math.ceil((buehnenIndex + 1) / SEITEN_GROESSE) * SEITEN_GROESSE);
  }, [buehnenIndex, sichtbar]);

  useEffect(() => {
    if (!gewaehlt) return;
    const controller = new AbortController();
    const url = archivPfadUrl(gewaehlt.pfad);
    setBildGross(false);
    setBildBereit(false);
    setBildStatus(url ? 'Bühnenbild wird geprüft …' : bildFehler(gewaehlt));
    if (!url) return () => controller.abort();
    fetch(url, { method: 'HEAD', signal: AbortSignal.any([controller.signal, AbortSignal.timeout(10000)]) })
      .then(response => {
        if (controller.signal.aborted) return;
        const typ = response.headers.get('content-type');
        if (!response.ok) throw new Error(`Das gespeicherte Bühnenbild antwortet mit HTTP ${response.status}.`);
        if (typ && !typ.startsWith('image/')) throw new Error(`Das Archiv liefert statt eines Bildes „${typ}“ aus.`);
        if (controller.signal.aborted) return;
        setBildBereit(true);
        setBildStatus(null);
      })
      .catch(error => {
        if (controller.signal.aborted || error?.name === 'AbortError') return;
        const grund = error?.name === 'TimeoutError'
          ? 'Das gespeicherte Bühnenbild konnte nicht rechtzeitig geprüft werden.'
          : error instanceof Error ? error.message : String(error);
        setDefekteBilder(alt => new Set(alt).add(gewaehlt.id));
        setBildBereit(false);
        setBildStatus(grund);
      });
    return () => controller.abort();
  }, [gewaehlt?.id]);

  const aufnahmeWaehlen = useCallback((aufnahme: HistorienAufnahme) => setAuswahl(aufnahme.id), []);
  const bildAlsDefektMelden = useCallback((aufnahme: HistorienAufnahme) => {
    setDefekteBilder(alt => new Set(alt).add(aufnahme.id));
    if (aufnahme.id === auswahl) {
      setBildGross(false);
      setBildBereit(false);
      setBildStatus('Das gespeicherte Bühnenbild konnte nicht dargestellt werden.');
    }
  }, [auswahl]);

  const gespeicherteAnzahl = alleAufnahmen.filter(aufnahme => !!aufnahme.pfad).length;

  return (
    <section data-ai-id="guide-buehnenhistorie" aria-label="Bühnenhistorie">
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <button type="button" data-ai-id="guide-historie-schliessen" onClick={onSchliessen}
          style={{ background: 'transparent', border: '1px solid var(--tn-border)', borderRadius: 6, color: 'var(--tn-blue)', cursor: 'pointer', padding: '3px 8px' }}>
          ← zurück zur aktuellen Bühne
        </button>
        <strong style={{ marginLeft: 2 }}>Bühnenhistorie</strong>
        <span style={{ color: 'var(--tn-text-muted)', fontSize: 10 }}>
          {rueckblicke ? `${rueckblicke.length} besuchte Bühnen · ${gespeicherteAnzahl} gespeicherte Bilder` : 'lädt …'}
        </span>
      </div>

      <div style={{ border: '1px solid var(--tn-border)', borderRadius: 8, padding: 8, marginBottom: 8, background: 'rgba(122,162,247,.05)' }}>
        <div style={{ fontWeight: 700, color: 'var(--tn-text)' }}>Historischer Rückblick</div>
        <div style={{ color: 'var(--tn-text-muted)', fontSize: 10.5, marginTop: 2 }}>
          Gespeicherte Sichtprüfungen, Fassungen und Abschlussstände. Die aktuelle Bühne bleibt erhalten; hier wird keine alte Sitzung gestartet oder verändert.
        </div>
      </div>

      {fehler && <div role="alert" style={{ color: 'var(--tn-red)', marginBottom: 8 }}>
        Historie nicht geladen: {fehler}{' '}
        <button type="button" data-ai-id="guide-historie-retry" onClick={() => { setRueckblicke(null); setLadeVersuch(v => v + 1); }}>Erneut laden</button>
      </div>}
      {!fehler && !rueckblicke && <div role="status">Besuchte Bühnen werden geladen …</div>}
      {rueckblicke?.length === 0 && <div>Es gibt noch keine Bühne mit einer gespeicherten Sichtprüfung oder einem Abschlussbild.</div>}

      {gewaehlt && gewaehltRueckblick && (
        <div data-ai-id="guide-historie-auswahl" style={{ border: '1px solid var(--tn-border)', borderRadius: 8, padding: 8, marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button type="button" aria-label="Frühere gespeicherte Aufnahme" disabled={index >= alleAufnahmen.length - 1}
              onClick={() => aufnahmeWaehlen(alleAufnahmen[index + 1] ?? gewaehlt)}>← frühere Aufnahme</button>
            <button type="button" aria-label="Neuere gespeicherte Aufnahme" disabled={index <= 0}
              onClick={() => aufnahmeWaehlen(alleAufnahmen[index - 1] ?? gewaehlt)}>neuere Aufnahme →</button>
            <span style={{ marginLeft: 'auto', color: 'var(--tn-text-muted)', fontSize: 10 }}>{index + 1} / {alleAufnahmen.length}</span>
          </div>
          <h3 style={{ fontSize: 13, margin: '8px 0 2px' }}>{gewaehlt.buehne.titel}</h3>
          <div style={{ color: 'var(--tn-text-muted)', fontSize: 10.5 }}>
            {aufnahmeName(gewaehlt)} · {datum(gewaehlt.am)} · {gewaehlt.buehne.haus === 'partner' ? 'Haus Partner' : 'Haus Dev'}
          </div>
          <div style={{ color: 'var(--tn-text-muted)', fontSize: 10, marginTop: 3 }}>
            Fassung {fassungsIndex + 1} von {gewaehltRueckblick.aufnahmen.length} dieser Bühne
          </div>
          {gewaehltRueckblick.aufnahmen.length > 1 && (
            <div aria-label="Fassungen dieser Bühne" style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 3, marginTop: 7 }}>
              {gewaehltRueckblick.aufnahmen.map((aufnahme, i) => (
                <button key={aufnahme.id} type="button" onClick={() => aufnahmeWaehlen(aufnahme)} aria-pressed={aufnahme.id === gewaehlt.id}
                  style={{ fontSize: 9.5, flexShrink: 0, borderRadius: 4, cursor: 'pointer', border: `1px solid ${aufnahme.id === gewaehlt.id ? 'var(--tn-blue)' : 'var(--tn-border)'}`, background: aufnahme.id === gewaehlt.id ? 'rgba(122,162,247,.14)' : 'transparent', color: 'var(--tn-text)' }}>
                  {i + 1} · {aufnahme.art === 'abschluss' ? 'Abschluss' : datum(aufnahme.am)}
                </button>
              ))}
            </div>
          )}
          {gewaehlt.buehne.kette_an && (() => {
            const strang = strangVon(alle, gewaehlt.buehne.id);
            const zuBuehne = (id: string) => {
              const r = rueckblicke?.find(eintrag => eintrag.buehne.id === id);
              if (r) aufnahmeWaehlen(r.aufnahmen[0]);
            };
            const folge = umgesetztIn(alle, gewaehlt.buehne);
            const antworten = (gewaehlt.buehne.entscheidungen ?? []).filter(e => !!e.antwort);
            return (
              <div data-ai-id="guide-historie-strang" style={{ marginTop: 7 }}>
                {strang.length > 1 && (
                  <div aria-label="Strang dieser Bühne" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ color: 'var(--tn-text-muted)', fontSize: 10 }}>Strang:</span>
                    {strang.map((m, i) => {
                      const hatBild = !!rueckblicke?.some(eintrag => eintrag.buehne.id === m.id);
                      return (
                        <button key={m.id} type="button" disabled={!hatBild} onClick={() => zuBuehne(m.id)}
                          aria-pressed={m.id === gewaehlt.buehne.id}
                          title={hatBild ? m.titel : `${m.titel} — keine gespeicherte Aufnahme`}
                          style={{ fontSize: 9.5, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderRadius: 4,
                            cursor: hatBild ? 'pointer' : 'default', color: 'var(--tn-text)',
                            border: `1px solid ${m.id === gewaehlt.buehne.id ? 'var(--tn-blue)' : 'var(--tn-border)'}`,
                            background: m.id === gewaehlt.buehne.id ? 'rgba(122,162,247,.14)' : 'transparent' }}>
                          {i + 1}. {m.titel}
                        </button>
                      );
                    })}
                  </div>
                )}
                {antworten.map(e => (
                  <div key={e.id} data-ai-id="guide-historie-antwort" style={{ fontSize: 10.5, marginTop: 4 }}>
                    deine Antwort: {e.titel} → {antwortWort(e)}
                    {folge ? (
                      <button type="button" data-ai-id="guide-historie-umgesetzt" onClick={() => zuBuehne(folge.id)}
                        disabled={!rueckblicke?.some(eintrag => eintrag.buehne.id === folge.id)}
                        style={{ marginLeft: 6, fontSize: 10, color: 'var(--tn-blue)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
                        umgesetzt in: {folge.titel} →
                      </button>
                    ) : <span style={{ marginLeft: 6, color: 'var(--tn-text-muted)' }}>(noch keine Folgebühne)</span>}
                  </div>
                ))}
              </div>
            );
          })()}
          {bildStatus && <div role="alert" style={{ color: 'var(--tn-orange)', marginTop: 6 }}>{bildStatus}</div>}
          {bildBereit && archivPfadUrl(gewaehlt.pfad) && (
            <button type="button" aria-label="Bühnenbild vergrößern" onClick={() => setBildGross(true)}
              style={{ display: 'block', width: '100%', padding: 0, marginTop: 8, border: '1px solid var(--tn-border)', borderRadius: 6, overflow: 'hidden', background: 'var(--tn-bg-dark)', cursor: 'zoom-in' }}>
              <img src={archivPfadUrl(gewaehlt.pfad)!} alt={`${aufnahmeName(gewaehlt)}: ${gewaehlt.buehne.titel}`}
                onError={() => bildAlsDefektMelden(gewaehlt)}
                style={{ display: 'block', width: '100%', maxHeight: 240, objectFit: 'contain' }} />
            </button>
          )}
          {gewaehlt.buehne.sitzungen.length > 0 && (
            <div style={{ marginTop: 7, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <span style={{ color: 'var(--tn-text-muted)', fontSize: 10 }}>Vorhandene Gesprächshistorie:</span>
              {gewaehlt.buehne.sitzungen.map(sitzung => {
                const berater = gewaehlt.buehne.berater?.find(eintrag => eintrag.sitzung === sitzung);
                return berater?.konto ? (
                  <a key={sitzung} href={`/?panel=berater-chat&buehne=${encodeURIComponent(gewaehlt.buehne.id)}&sitzung=${encodeURIComponent(sitzung)}`}
                    target="_blank" rel="noopener noreferrer" style={{ color: 'var(--tn-blue)', fontSize: 10.5 }}>
                    Gespräch {sitzung.slice(0, 8)} lesen
                  </a>
                ) : <span key={sitzung} title={sitzung} style={{ color: 'var(--tn-text-muted)', fontSize: 10 }}>
                  Gespräch {sitzung.slice(0, 8)} nicht mehr zugeordnet
                </span>;
              })}
            </div>
          )}
        </div>
      )}

      {rueckblicke && rueckblicke.length > 0 && (
        <div data-ai-id="guide-historie-galerie" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 7 }}>
          {rueckblicke.slice(0, sichtbar).map(rueckblick => {
            const bild = rueckblick.aufnahmen.find(aufnahme => !!aufnahme.pfad && !defekteBilder.has(aufnahme.id)) ?? rueckblick.aufnahmen[0];
            const url = archivPfadUrl(bild.pfad);
            return (
              <button key={rueckblick.buehne.id} type="button" onClick={() => aufnahmeWaehlen(rueckblick.aufnahmen[0])}
                aria-pressed={rueckblick.buehne.id === gewaehlt?.buehne.id} data-guide-historie={url ? 'foto' : 'ohne-foto'}
                style={{ textAlign: 'left', cursor: 'pointer', padding: 6, borderRadius: 7, color: 'var(--tn-text)', background: rueckblick.buehne.id === gewaehlt?.buehne.id ? 'rgba(122,162,247,.14)' : 'transparent', border: `1px solid ${rueckblick.buehne.id === gewaehlt?.buehne.id ? 'var(--tn-blue)' : 'var(--tn-border)'}` }}>
                {url ? (
                  <img src={url} alt={`Gespeicherte Aufnahme: ${rueckblick.buehne.titel}`} loading="lazy" onError={() => bildAlsDefektMelden(bild)}
                    style={{ display: 'block', width: '100%', height: 82, objectFit: 'cover', borderRadius: 4, background: 'var(--tn-bg-dark)', marginBottom: 5 }} />
                ) : (
                  <div title={bildFehler(bild)} style={{ height: 82, display: 'grid', placeItems: 'center', borderRadius: 4, background: 'rgba(224,175,104,.08)', color: 'var(--tn-orange)', fontSize: 10, textAlign: 'center', marginBottom: 5, padding: 5 }}>
                    Kein historisches Bild
                  </div>
                )}
                <div style={{ fontWeight: 650, fontSize: 10.5, lineHeight: 1.3 }}>{rueckblick.buehne.titel}</div>
                <div style={{ color: 'var(--tn-text-muted)', fontSize: 9.5, marginTop: 3 }}>
                  {datum(rueckblick.aufnahmen[0].am)} · {rueckblick.aufnahmen.length} {rueckblick.aufnahmen.length === 1 ? 'Fassung' : 'Fassungen'}
                </div>
              </button>
            );
          })}
          {sichtbar < rueckblicke.length && (
            <button type="button" data-ai-id="guide-historie-mehr" onClick={() => setSichtbar(anzahl => anzahl + SEITEN_GROESSE)}
              style={{ minHeight: 110, borderRadius: 7, border: '1px dashed var(--tn-border)', background: 'transparent', color: 'var(--tn-blue)', cursor: 'pointer' }}>
              Weitere {Math.min(SEITEN_GROESSE, rueckblicke.length - sichtbar)} Bühnen anzeigen
            </button>
          )}
        </div>
      )}
      {bildGross && gewaehlt && archivPfadUrl(gewaehlt.pfad) && (
        <div role="dialog" aria-modal="true" aria-label="Historisches Bühnenbild vergrößert"
          onClick={() => setBildGross(false)}
          style={{ position: 'fixed', inset: 12, zIndex: 10000, display: 'grid', placeItems: 'center', background: 'color-mix(in srgb, var(--tnfh-080a12) 94%, transparent)', border: '1px solid var(--tn-border)', borderRadius: 8, padding: 34, cursor: 'zoom-out' }}>
          <button type="button" aria-label="Vergrößerung schließen" onClick={() => setBildGross(false)}
            style={{ position: 'absolute', top: 8, right: 8, border: '1px solid var(--tn-border)', borderRadius: 5, background: 'var(--tn-bg-dark)', color: 'var(--tn-text)', cursor: 'pointer' }}>✕</button>
          <img src={archivPfadUrl(gewaehlt.pfad)!} alt={`${aufnahmeName(gewaehlt)} vergrößert: ${gewaehlt.buehne.titel}`}
            onClick={event => event.stopPropagation()} onError={() => bildAlsDefektMelden(gewaehlt)}
            style={{ display: 'block', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </div>
      )}
    </section>
  );
}
